import React, { useState, useEffect, useRef } from 'react';
import Title from './Title';
import DateSelector from './Fields/DateSelector';
import TimeSelector from './Fields/TimeSelector';
import GuestNumberSelector from './Fields/GuestSelector';
import MaxGuestMessage from './Notifications/index';
import MededelingNotification from './Notifications/MededelingNotification';
import useReservations from './Hooks/useReservations';
import moment from 'moment-timezone';
import { isMededelingActive, formatDate, renderGeneralNotification } from './Utils/stepOneUtils';
import { getAvailableGiftcards } from './Utils/giftcards';
import './css/notifications.css';
import useNotificationMargin from './Hooks/useNotificationMargin';
import GiftcardSelection from './Components/GiftcardSelection';
import { findMainNotification, computeNotice } from './Utils/notifications';

// Helper to normalize text for comparing duplicates
const normalizeText = (str) =>
  (str || '')
    .toString()
    .replace(/<[^>]*>/g, '') // strip any HTML tags just in case
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const StepOne = ({
  title,
  timeblocks,
  formData,
  handleChange,
  resetFormDataFields,
  setCurrentStep,
  fields,
  generalNotification,
  expandedField,
  generalSettings,
  mededeling,
  restaurantData,
}) => {
  // Giftcard selection state
  const [giftcardChoice, setGiftcardChoice] = useState(null); // "Ja" or "Nee"
  const [selectedGiftcard, setSelectedGiftcard] = useState(null);

  // Get available giftcards
  const availableGiftcards = getAvailableGiftcards(restaurantData);

  const [currentExpandedField, setCurrentExpandedField] = useState(expandedField || 'guest');
  const [guests, setGuests] = useState(1);
  const { reservations, fetchReservationsForMonth } = useReservations();

  // Find main notification in mededeling array
  const mainNotification = findMainNotification(mededeling);

  // Determine which general message will be shown at the top (main > general)
  const topNotification =
    (mainNotification && mainNotification.trim() !== '')
      ? mainNotification
      : (generalNotification && generalNotification.trim() !== '')
        ? generalNotification
        : null;

  // Ref to store the previous number of guests
  const previousGuestsRef = useRef(formData['guests'] || 1);

  // Use maxGasten from generalSettings, default to 10 if not available
  const maxGuests = parseInt(generalSettings?.maxGasten, 10) || 10;

  const handleNext = (e) => {
    e.preventDefault();
    // Store the selected giftcard in formData before moving to step 2
    if (giftcardChoice === "Ja" && selectedGiftcard) {
      handleChange({
        target: { name: 'giftcard', value: selectedGiftcard }
      });
    } else {
      // If "Nee" or no selection, ensure giftcard is empty in formData
      handleChange({
        target: { name: 'giftcard', value: '' }
      });
    }
    setCurrentStep(2);
  };

  const timeField = fields.find((field) => field.id === 'time');
  const guestNumberField = fields.find((field) => field.id === 'guests');

  const numGuests = parseInt(formData[guestNumberField.id], 10) || 0;

  const { showNotice, type: noticeType } = computeNotice({ generalSettings, numGuests, maxGuests });

  // Determine if the time has been selected
  const isTimeSelected = !!formData[timeField.id];

  // Effect to handle guest changes
  useEffect(() => {
    const previousGuests = previousGuestsRef.current;
    const currentGuests = formData[guestNumberField.id];

    if (previousGuests !== currentGuests) {
      // Reset the date and time fields when guests change
      resetFormDataFields(['date', 'time']);
      // Fetch reservations again if guests change, as availability may have changed
      fetchReservationsForMonth();
    }

    // Update the ref with the current number of guests
    previousGuestsRef.current = currentGuests;
  }, [formData, guestNumberField.id, resetFormDataFields]);

  // Fetch reservations when component mounts or guests change
  useEffect(() => {
    fetchReservationsForMonth();
  }, [guests]);

  // Callback for when the month changes in the Calendar
  const handleMonthChange = (newMonthDate) => {
    // newMonthDate is a moment object representing the start of the displayed month
    fetchReservationsForMonth(newMonthDate);
  };

  // Determine if there is an active mededeling
  const activeMededeling = isMededelingActive(
    formData.date ? formatDate(formData.date) : null,
    formData.time,
    mededeling
  );

  // Only show the mededeling banner if it's not a duplicate of the top notification
  const shouldShowMededeling =
    !!activeMededeling &&
    normalizeText(activeMededeling) !== normalizeText(topNotification);

  useNotificationMargin(mainNotification, generalNotification);

  // Initialize giftcard state from formData on component mount
  useEffect(() => {
    if (formData.giftcard) {
      setGiftcardChoice("Ja");
      setSelectedGiftcard(formData.giftcard);
    }
  }, []);

  // Handlers for giftcard selection
  const handleGiftcardChoiceChange = (e) => {
    const value = e.target.value;
    setGiftcardChoice(value);
    // Reset giftcard selection if choice changes
    setSelectedGiftcard(null);
    // Reset form data related to guests, date, and time when giftcard choice changes
    resetFormDataFields(['guests', 'date', 'time', 'giftcard']);
    setGuests(1);
  };

  const handleSelectedGiftcardChange = (e) => {
    const value = e.target.value;
    setSelectedGiftcard(value);
    // Store the selected giftcard in formData immediately
    handleChange({
      target: { name: 'giftcard', value: value }
    });
    // Reset form data related to date and time when giftcard selection changes
    resetFormDataFields(['guests', 'date', 'time']);
    setGuests(1);
  };

  // Inline styles moved into GiftcardSelection component to keep this file lean

  return (
    <form onSubmit={handleNext}>
      <Title title={title} subtitle="Stap 1/3" />

      {/* Use mainNotification if available, otherwise fall back to generalNotification */}
      {topNotification && topNotification.trim() !== '' && (
        <div className="general-notification">
          {renderGeneralNotification(topNotification)}
        </div>
      )}

      {shouldShowMededeling && (
        <MededelingNotification message={activeMededeling} />
      )}

      <GiftcardSelection
        availableGiftcards={availableGiftcards}
        giftcardChoice={giftcardChoice}
        selectedGiftcard={selectedGiftcard}
        onChoiceChange={handleGiftcardChoiceChange}
        onGiftcardChange={handleSelectedGiftcardChange}
      />

      {/* Only reveal guest selector and subsequent fields if giftcard selection is complete */}
      {(!availableGiftcards ||
        availableGiftcards.length === 0 ||
        giftcardChoice === "Nee" ||
        (giftcardChoice === "Ja" && selectedGiftcard)) && (
        <>
          <GuestNumberSelector
            setGuests={setGuests}
            formData={formData}
            handleChange={handleChange}
            field={guestNumberField}
            expanded={currentExpandedField === 'guest'}
            setCurrentExpandedField={setCurrentExpandedField}
            maxGuests={maxGuests}
            generalSettings={generalSettings}
          />

          {showNotice && (
            <MaxGuestMessage
              maxGuests={maxGuests}
              noticePhoneNumber={
                generalSettings?.showNoticeForMaxGuests === 'Ja'
                  ? generalSettings.noticePhoneNumber
                  : ''
              }
              type={noticeType}
            />
          )}

          {!showNotice && formData[guestNumberField.id] && (
            <DateSelector
              guests={numGuests}
              formData={formData}
              handleChange={handleChange}
              resetFormDataFields={resetFormDataFields}
              expanded={currentExpandedField === 'date'}
              setCurrentExpandedField={setCurrentExpandedField}
              timeblocks={timeblocks}
              restaurantData={restaurantData}
              reservations={reservations}
              onMonthChange={handleMonthChange}
              giftcard={giftcardChoice === "Ja" ? selectedGiftcard : null}
            />
          )}

          {!showNotice && formData.date && (
            <TimeSelector
              guests={numGuests}
              formData={formData}
              handleChange={handleChange}
              field={timeField}
              selectedDate={formData.date}
              expanded={currentExpandedField === 'time'}
              setCurrentExpandedField={setCurrentExpandedField}
              timeblocks={timeblocks}
              restaurantData={restaurantData}
              reservations={reservations}
              giftcard={giftcardChoice === "Ja" ? selectedGiftcard : null}
            />
          )}

          {!showNotice && isTimeSelected && (
            <button type="submit" className="submit-button">
              Volgende
            </button>
          )}
        </>
      )}
    </form>
  );
};

export default StepOne;
